import { Button, Form, Input } from 'antd';
import cls from 'clsx';
import { useEffect, useState } from 'react';
import type { ChangeEvent, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/api/types';
import { images } from '@/shared/assets/images/images';
import { validateEmail } from '@/shared/lib/email';
import { Fade } from '@/shared/ui/Fade';

import s from '@/shared/ui/AuthForm.module.css';

interface AddEmailFormValues {
  email: string;
  accept?: boolean;
  confirm?: string;
}

interface AddEmailFormProps {
  onSubmit: (values: AddEmailFormValues) => void;
  // The failed request, as `parseApiError` returns it
  error?: ApiError | null;
}

export function AddEmailForm({ error, ...props }: AddEmailFormProps) {
  const [errorText, setErrorText] = useState<ReactNode>(null);

  const [isVisibleBtn, setIsVisibleBtn] = useState(false);
  const [isBackError, setIsBackError] = useState(false);

  const [form] = Form.useForm<AddEmailFormValues>();
  const { t } = useTranslation();

  useEffect(() => {
    if (error) {
      setErrorText(error.message);
      setIsBackError(true);
    }
  }, [error]);

  const onFinish = (values: AddEmailFormValues) => {
    const data = { ...values };
    delete data.accept;
    delete data.confirm;
    props.onSubmit(values);
  };

  const onFinishFailed = ({
    errorFields,
  }: {
    errorFields?: { errors: string[] }[];
  }) => {
    if (errorFields) setErrorText(errorFields[0]?.errors);
  };

  function handleClearError(_e: unknown, field: keyof AddEmailFormValues) {
    form.setFields([
      {
        name: field,
        errors: [],
      },
    ]);
    let newError: string | null = null;
    const errorList = form.getFieldsError();

    errorList.forEach((field) => {
      if (field.errors.length && !newError) newError = field.errors[0] ?? null;
    });
    setIsBackError(false);
    setErrorText(newError);
  }

  function handleChange(e: ChangeEvent<HTMLInputElement>) {
    const { name, value } = e.target;
    if (name === 'email') {
      if (validateEmail(value)) {
        if (!isVisibleBtn) setIsVisibleBtn(true);
      } else {
        if (isVisibleBtn) setIsVisibleBtn(false);
      }
    }
  }

  return (
    <Form
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      validateTrigger="onSubmit"
      className={s.form}
      layout="vertical"
    >
      <Form.Item noStyle shouldUpdate={true}>
        {() => (
          <div className={cls(s.errorText, !errorText && s.errorTextEmpty)}>
            {isBackError ? (
              <>
                {errorText + ` ${t('Kindly try another one or')} `}{' '}
                <Link to="/login">{t('Sign In')}</Link>
              </>
            ) : (
              errorText
            )}
          </div>
        )}
      </Form.Item>

      <Form.Item
        label=""
        name="email"
        validateStatus={isBackError ? 'error' : ''}
        rules={[
          { required: true, message: t('Please insert your Email') },
          { type: 'email', message: t('The email you entered is incorrect') },
        ]}
      >
        <Input
          placeholder={t('Email Address')}
          name="email"
          type="email"
          onChange={(e) => {
            handleChange(e);
            handleClearError(e, 'email');
          }}
          prefix={<img src={images.email} alt="" />}
        />
      </Form.Item>

      <Fade inProp={isVisibleBtn} className={s.pushDown}>
        <Button
          type="primary"
          htmlType="submit"
          className={s.submit}
          disabled={!isVisibleBtn}
        >
          {t('Create Account')}
        </Button>
      </Fade>
    </Form>
  );
}
